import { KnownInboxLabel } from "@qw/qw-common";
import { useGridForm } from "@qw/ui";
import { type FC, useMemo } from "react";
import { BulkActions, type BulkActionsMenuItem } from "@/components/bulk-actions";
import { useInboxQueryStates } from "@/providers/inbox-query-states-provider";
import { useBatchAddLabelToConversations } from "@/providers/use-batch-add-label-to-conversations";
import { useBatchDeleteConversations } from "@/providers/use-batch-delete-conversations";
import { useBatchRemoveLabelFromConversations } from "@/providers/use-batch-remove-label-from-conversations";
import { useConversations } from "@/providers/use-conversations";

export const InboxBulkActions: FC = () => {
  const { conversationId, setConversationId } = useInboxQueryStates();
  const { selectedRowIds } = useGridForm();
  const { conversations } = useConversations();
  const [batchAddLabel, { loading: isBulkAddLabelLoading }] = useBatchAddLabelToConversations();
  const [batchRemoveLabel, { loading: isBulkRemoveLabelLoading }] = useBatchRemoveLabelFromConversations();
  const [batchDeleteConversations, { loading: isBulkDeleteConversationsLoading }] = useBatchDeleteConversations();

  const isLoading = isBulkAddLabelLoading || isBulkRemoveLabelLoading || isBulkDeleteConversationsLoading;

  const actions = useMemo(() => {
    const selectedConversations = conversations.filter((conversation) => selectedRowIds?.includes(conversation.id));

    const hasUnread = selectedConversations.some((conversation) =>
      conversation.labels.includes(KnownInboxLabel.UNREAD)
    );
    const hasInbox = selectedConversations.some((conversation) => conversation.labels.includes(KnownInboxLabel.INBOX));
    const hasTrash = selectedConversations.every((conversation) => conversation.labels.includes(KnownInboxLabel.TRASH));
    const hasStarred = selectedConversations.some((conversation) =>
      conversation.labels.includes(KnownInboxLabel.STARRED)
    );

    const items: BulkActionsMenuItem[] = [];
    const moreItems: BulkActionsMenuItem[] = [];

    if (hasInbox) {
      items.push({
        icon: "archive",
        label: "Archive",
        onClick: async () => {
          await batchRemoveLabel({
            conversations: selectedConversations,
            label: KnownInboxLabel.INBOX,
            successMessage: `Archived ${selectedConversations.length} conversations`,
            errorMessage: `Failed to archive ${selectedConversations.length} conversations`,
          });

          if (conversationId && selectedRowIds.includes(conversationId)) {
            setConversationId(null, { history: "replace" });
          }
        },
      });
    } else {
      items.push({
        icon: "move_to_inbox",
        label: "Move to inbox",
        onClick: () =>
          batchAddLabel({
            conversations: selectedConversations,
            label: KnownInboxLabel.INBOX,
            successMessage: `Moved ${selectedConversations.length} conversations to inbox`,
            errorMessage: `Failed to move ${selectedConversations.length} conversations to inbox`,
          }),
      });
    }

    if (hasUnread) {
      items.push({
        icon: "drafts",
        label: "Mark as read",
        onClick: () =>
          batchRemoveLabel({
            conversations: selectedConversations,
            label: KnownInboxLabel.UNREAD,
            successMessage: `Marked ${selectedConversations.length} conversations as read`,
            errorMessage: `Failed to mark ${selectedConversations.length} conversations as read`,
          }),
      });
    } else {
      items.push({
        icon: "mark_email_unread",
        label: "Mark as unread",
        onClick: async () => {
          await batchAddLabel({
            conversations: selectedConversations,
            label: KnownInboxLabel.UNREAD,
            successMessage: `Marked ${selectedConversations.length} conversations as unread`,
            errorMessage: `Failed to mark ${selectedConversations.length} conversations as unread`,
          });

          if (conversationId && selectedRowIds.includes(conversationId)) {
            setConversationId(null, { history: "replace" });
          }
        },
      });
    }

    if (hasStarred) {
      moreItems.push({
        icon: "star",
        label: "Remove star",
        onClick: () =>
          batchRemoveLabel({
            conversations: selectedConversations,
            label: KnownInboxLabel.STARRED,
            successMessage: `Removed star from ${selectedConversations.length} conversations`,
            errorMessage: `Failed to remove star from ${selectedConversations.length} conversations`,
          }),
        iconProps: { className: "filled" } as BulkActionsMenuItem["iconProps"],
      });
    } else {
      moreItems.push({
        icon: "star",
        label: "Add star",
        onClick: () =>
          batchAddLabel({
            conversations: selectedConversations,
            label: KnownInboxLabel.STARRED,
            successMessage: `Added star to ${selectedConversations.length} conversations`,
            errorMessage: `Failed to add star to ${selectedConversations.length} conversations`,
          }),
      });
    }

    if (hasTrash) {
      moreItems.push({
        icon: "delete",
        label: "Delete forever",
        onClick: () => batchDeleteConversations(selectedRowIds),
      });
    } else {
      moreItems.push({
        icon: "delete",
        label: "Delete",
        onClick: () =>
          batchAddLabel({
            conversations: selectedConversations,
            label: KnownInboxLabel.TRASH,
            successMessage: `Moved ${selectedConversations.length} conversations to trash`,
            errorMessage: `Failed to move ${selectedConversations.length} conversations to trash`,
          }),
      });
    }

    // TODO: Implement custom labels
    // moreItems.push(
    //   {
    //     icon: "label",
    //     label: "Label as",
    //     onClick: () => {},
    //   },
    //   {
    //     icon: "move_item",
    //     label: "Move to",
    //     onClick: () => {},
    //   }
    // );

    return { items, moreItems };
  }, [
    conversationId,
    setConversationId,
    conversations,
    selectedRowIds,
    batchAddLabel,
    batchRemoveLabel,
    batchDeleteConversations,
  ]);

  return <BulkActions actions={actions} isLoading={isLoading} />;
};
